import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../services/api";
import { fileService } from "../services";
import { useAuth } from "../context/AuthContext";
import {
  ArrowBack,
  Upload,
  Description,
  Delete,
  CloudUpload,
} from "@mui/icons-material";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import PageSkeleton from "../components/ui/PageSkeleton";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  document_type: Yup.string().required("Document type is required"),
  category: Yup.string().required("Category is required"),
  description: Yup.string(),
  expiry_date: Yup.string().nullable(),
});

export default function DocumentEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isAdmin = userRole === "ADMIN" || userRole === "HR";
  const isManager = userRole === "MANAGER" || user?.role === "manager";
  const canSelectEmployee = isAdmin || isManager;

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [document, setDocument] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);

  useEffect(() => {
    if (!id || id === "undefined") {
      toast.error("Invalid document ID");
      navigate("/documents");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all documents and find the one with matching ID
        const allDocsResponse = await api.get("/documents");
        const allDocs = Array.isArray(allDocsResponse.data) 
          ? allDocsResponse.data 
          : Array.isArray(allDocsResponse.data?.data)
          ? allDocsResponse.data.data
          : [];
        const foundDoc = allDocs.find(d => {
          const docId = (d.id || d._id)?.toString();
          return docId === id?.toString();
        });
        
        if (!foundDoc) {
          toast.error("Document not found");
          navigate("/documents");
          return;
        }
        setDocument(foundDoc);

        // Fetch employees if admin/manager
        if (canSelectEmployee) {
          const empResponse = await api.get("/employees");
          const payload = empResponse.data;
          const employeeList = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
            ? payload.data
            : [];
          
          const currentUserId = user?.id || user?._id;
          const currentUserEmployee = employeeList.find(
            (e) => (e.user_id?.id || e.user_id?._id || e.user_id)?.toString() === currentUserId?.toString()
          );
          
          if (currentUserEmployee) {
            setCurrentEmployee(currentUserEmployee);
          }
          
          setEmployees(employeeList);
        } else if (isEmployee) {
          const empResponse = await api.get("/employees");
          const payload = empResponse.data;
          const employeeList = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
            ? payload.data
            : [];
          const currentUserId = user?.id || user?._id;
          const emp = employeeList.find(
            (e) => (e.user_id?.id || e.user_id?._id || e.user_id)?.toString() === currentUserId?.toString()
          );
          setCurrentEmployee(emp);
        }
      } catch (err) {
        console.error("Failed to fetch document", err);
        toast.error("Failed to load document");
        navigate("/documents");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, canSelectEmployee, isEmployee, user]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Please upload a PDF or image file (JPEG, PNG, GIF, WebP)");
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      setFile(selectedFile);

      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
  };

  const uploadFile = async (fileToUpload) => {
    const formData = new FormData();
    formData.append("file", fileToUpload);

    try {
      const response = await api.post("/documents/upload-file", formData);

      if (response.data?.file_url) {
        return {
          file_url: response.data.file_url,
          file_name: response.data.filename || fileToUpload.name,
          file_size: response.data.file_size || fileToUpload.size,
          mime_type: response.data.mime_type || fileToUpload.type,
        };
      }
      throw new Error("No file URL returned");
    } catch (err) {
      console.error("File upload error:", err);
      throw new Error(err.response?.data?.error || "Failed to upload file");
    }
  };

  const formik = useFormik({
    initialValues: {
      title: document?.title || "",
      document_type: document?.document_type || "other",
      category: document?.category || "hr",
      description: document?.description || "",
      expiry_date: document?.expiry_date 
        ? new Date(document.expiry_date).toISOString().split("T")[0]
        : "",
      employee_id: document?.employee_id?.id || document?.employee_id?._id || document?.employee_id || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSaving(true);

        let fileData = null;
        if (file) {
          // Upload new file if provided
          fileData = await uploadFile(file);
        }

        // Prepare document data
        const documentData = {
          title: values.title,
          document_type: values.document_type,
          category: values.category,
          description: values.description || null,
          expiry_date: values.expiry_date || null,
          employee_id: isEmployee
            ? (currentEmployee?.id || currentEmployee?._id)
            : canSelectEmployee
            ? values.employee_id
            : document?.employee_id?.id || document?.employee_id?._id || document?.employee_id,
        };

        // Only update file fields if a new file was uploaded
        if (fileData) {
          documentData.file_url = fileData.file_url;
          documentData.file_name = fileData.file_name;
          documentData.file_size = fileData.file_size;
          documentData.mime_type = fileData.mime_type;
        }

        // Update document
        await api.put(`/documents/${id}`, documentData);

        toast.success("Document updated successfully!");
        navigate("/documents");
      } catch (err) {
        console.error("Update error:", err);
        toast.error(err.response?.data?.error || err.message || "Failed to update document");
      } finally {
        setSaving(false);
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return <PageSkeleton type="form" />;
  }

  if (!document) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card text-center py-12">
          <p className="text-gray-500 text-lg">Document not found</p>
          <Button onClick={() => navigate("/documents")} className="mt-4">
            Back to Documents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
              Edit Document
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {isEmployee 
                ? "Update your document" 
                : canSelectEmployee 
                ? "Update document information"
                : "Update document"}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate("/documents")}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <ArrowBack className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Back</span>
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="card p-4 sm:p-6">
          {/* Employee Selection (Admin and Manager) */}
          {canSelectEmployee && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee <span className="text-red-500">*</span>
              </label>
              <select
                name="employee_id"
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="input-field w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => {
                  const empId = emp.id || emp._id;
                  const empName = emp.user_id?.name || "Unknown";
                  const isCurrentUser = currentEmployee && (empId === (currentEmployee.id || currentEmployee._id));
                  return (
                    <option
                      key={empId}
                      value={empId}
                    >
                      {empName}{isCurrentUser ? " (You)" : ""}
                    </option>
                  );
                })}
              </select>
              {formik.touched.employee_id && formik.errors.employee_id && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.employee_id}</p>
              )}
            </div>
          )}

          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter document title"
              required
            />
            {formik.touched.title && formik.errors.title && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.title}</p>
            )}
          </div>

          {/* Document Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              name="document_type"
              value={formik.values.document_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="input-field w-full"
              required
            >
              <option value="other">Other</option>
              <option value="id_proof">ID Proof</option>
              <option value="address_proof">Address Proof</option>
              <option value="educational_certificate">Educational Certificate</option>
              <option value="employment_contract">Employment Contract</option>
              <option value="nda">NDA</option>
              <option value="policy_document">Policy Document</option>
            </select>
            {formik.touched.document_type && formik.errors.document_type && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.document_type}</p>
            )}
          </div>

          {/* Category */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="input-field w-full"
              required
            >
              <option value="hr">HR</option>
              <option value="finance">Finance</option>
              <option value="compliance">Compliance</option>
              <option value="legal">Legal</option>
            </select>
            {formik.touched.category && formik.errors.category && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.category}</p>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter document description (optional)"
              rows="4"
              className="input-field w-full"
            />
            {formik.touched.description && formik.errors.description && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.description}</p>
            )}
          </div>

          {/* Expiry Date */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Expiry Date (Optional)
            </label>
            <Input
              type="date"
              name="expiry_date"
              value={formik.values.expiry_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.expiry_date && formik.errors.expiry_date && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.expiry_date}</p>
            )}
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              File {file ? "(New file)" : "(Current file will be kept)"}
            </label>
            {document.file_url && !file && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <Description className="w-6 h-6 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-700 truncate">{document.file_name || "Current file"}</p>
                    <p className="text-sm text-gray-500">
                      {document.file_size ? `${(document.file_size / 1024 / 1024).toFixed(2)} MB` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileService.openDownload(document.file_url, (err) => toast.error(err))}
                    className="text-primary hover:underline text-sm"
                  >
                    View
                  </button>
                </div>
              </div>
            )}
            {!file ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-4"
                >
                  <CloudUpload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                  <div>
                    <span className="text-primary font-semibold">Click to upload new file</span> or drag and drop
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    PDF, JPEG, PNG, GIF, WebP (Max 10MB)
                  </p>
                </label>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Description className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-700 truncate">{file.name}</p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                  >
                    <Delete className="w-5 h-5" />
                  </button>
                </div>
                {filePreview && (
                  <div className="mt-4">
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="max-w-full max-h-48 sm:max-h-64 rounded-lg mx-auto"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/documents")}
              disabled={saving}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Update Document
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

